import { MailOutlined, UserOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Spin,
  Typography
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { genericApi } from '~/api'
import { TimezoneSelect } from '~/components/TimezoneSelect'
import {
  JOIN_TYPE_HANDS_ON,
  JOIN_TYPE_RECORDING,
  JOIN_TYPE_VIEWER
} from '~/utils/constants'
import CourseRegistrationLayout from './CourseRegistrationLayout'
import { useRegisterMutation } from './mutation'
import { useCountryQuery } from './query'
import './styles.scss'

const { Title, Paragraph } = Typography
const { Option } = Select

const CourseRegistrationForm = () => {
  const { token } = useParams()
  const registerMutation = useRegisterMutation(token)
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['courses', token],
    queryFn: async () => {
      const { data } = await genericApi.getCourse(token)
      return data
    }
  })

  const { data: country } = useCountryQuery()
  const course = data?.data
  const shouldWaitlist = data?.should_waitlist
  console.log('🚀 ~ CourseRegistrationForm ~ shouldWaitlist:', shouldWaitlist)
  if (isLoading) {
    return (
      <div className='centered-container'>
        <Spin size='large' />
      </div>
    )
  }

  if (course == null) {
    return (
      <div className='signup-form'>
        <Card
          variant='outlined'
          style={{ maxWidth: 350, margin: '40px auto auto' }}
        >
          <Title level={3} style={{ margin: 'auto' }}>
            This course is not available
          </Title>
        </Card>
      </div>
    )
  }

  if (course.registration_form?.is_closed) {
    return (
      <div className='signup-form'>
        <Card
          variant='outlined'
          style={{ maxWidth: 350, margin: '40px auto auto' }}
        >
          <Title level={4} style={{ margin: 'auto' }}>
            <p>The event registration is now closed.</p>
          </Title>
          <p>
            Another CMR course is scheduled. Follow{' '}
            <a
              href='https://www.linkedin.com/company/radup/'
              target='_blank'
              rel='noreferrer'
            >
              RadUp on LinkedIn
            </a>
          </p>
        </Card>
      </div>
    )
  }

  if (registerMutation.error) {
    return (
      <div className='signup-form'>
        <Title
          className='course-name'
          level={3}
          style={{ marginBottom: 40, marginTop: 40 }}
        >
          {course?.name}
        </Title>
        <Card
          variant='outlined'
          style={{ maxWidth: 350, margin: '40px auto auto' }}
        >
          <Paragraph style={{ margin: 'auto' }}>
            {registerMutation.error.response?.data?.message ??
              'Something went wrong, please try again later'}
          </Paragraph>
        </Card>
      </div>
    )
  }

  const bgColor = course.registration_form.settings?.background || '#23426B'

  if (!data) {
    return null
  }

  return (
    <CourseRegistrationLayout course={course}>
      {shouldWaitlist && (
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <p>
            <i>
              This course is full. Feel free to register for the waitlist.{' '}
              <br />
              If a seat becomes available you will be contacted.
            </i>
          </p>
        </div>
      )}
      <div style={{ textAlign: 'center' }}>
        <Title level={2}>
          {shouldWaitlist ? 'Waitlist registration' : 'Register'}
        </Title>
      </div>

      {course?.is_closed_for_registration && (
        <div style={{ textAlign: 'center' }}>
          <Title level={5}>
            Kindly note that registration for this workshop has been closed.
            Apologies for any inconvenience.
          </Title>
        </div>
      )}

      {!course?.is_closed_for_registration && (
        <Form
          name='normal_register'
          className='register-form'
          layout='vertical'
          onFinish={(values) => {
            const payload = {
              ...values,
              is_waitlist: shouldWaitlist
            }
            registerMutation.mutate(payload, {
              onSuccess: (data) => {
                if (data.is_waitlist) {
                  navigate(
                    `/registrations/success?course_name=${course?.name}&waitlist=true`
                  )
                } else if (data.payment_amount > 0) {
                  navigate(`/registrations/${data.id}/summary`)
                } else {
                  navigate(`/registrations/success?course_name=${course?.name}`)
                }
              }
            })
          }}
          initialValues={{
            country_code: data.country_code,
            prefer_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            join_type: data.out_of_handon_slots === true ? 'viewer' : null,
            is_waitlist: shouldWaitlist === true

            // TODO: TEST
            // first_name: 'vu',
            // last_name: 'nguyen',
            // email: 'vu@manifera.com',
            // email_confirmation: 'vu@manifera.com',
            // country_code: 'VN',
            // institution: 'Institution name'
          }}
        >
          <Row gutter={10}>
            <Col md={12} xs={24}>
              <Form.Item
                label='First Name'
                name='first_name'
                rules={[
                  { required: true, message: 'Please input your first name!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined className='site-form-item-icon' />}
                  placeholder='First Name'
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                name='last_name'
                label='Last Name'
                rules={[
                  { required: true, message: 'Please input your last name!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined className='site-form-item-icon' />}
                  placeholder='Last Name'
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                name='email'
                label='Email'
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'The input is not valid email' }
                ]}
              >
                <Input
                  prefix={<MailOutlined className='site-form-item-icon' />}
                  placeholder='Email'
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label='Confirm Email'
                name='email_confirmation'
                rules={[
                  { required: true, message: 'Please confirm your email!' },
                  { type: 'email', message: 'The input is not valid email' },
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (!value || getFieldValue('email') === value) {
                        return
                      }

                      throw new Error(
                        'The email that you entered do not match!'
                      )
                    }
                  })
                ]}
              >
                <Input
                  prefix={<MailOutlined className='site-form-item-icon' />}
                  placeholder='Confirm Email'
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                name='country_code'
                label='Country'
                rules={[
                  { required: true, message: 'Please select your country!' }
                ]}
              >
                <Select
                  placeholder='Country'
                  showSearch
                  optionFilterProp='children'
                  filterOption={(input, option) => {
                    const value = option?.children || ''
                    return value.toLowerCase().includes(input.toLowerCase())
                  }}
                  allowClear
                >
                  {country?.map((item) => (
                    <Option value={item.code} key={item.code}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                name='institution'
                label='Name Institution'
                rules={[
                  { required: true, message: 'Name Institution is required' }
                ]}
              >
                <Input placeholder='Name Institution' />
              </Form.Item>
            </Col>
            {course.registration_form?.settings?.timezone && (
              <Col md={12} xs={24}>
                <Form.Item
                  name='prefer_timezone'
                  label="I'm joining from time zone"
                  rules={[
                    { required: true, message: 'Please select your timezone' }
                  ]}
                >
                  <TimezoneSelect placeholder="I'm joining from time zone" />
                </Form.Item>
              </Col>
            )}
            {course.registration_form?.settings?.join_type && (
              <Col md={24} xs={24}>
                <Form.Item
                  name='join_type'
                  label='Please select'
                  rules={[{ required: true, message: 'Please select' }]}
                >
                  <Radio.Group>
                    <Radio
                      value={JOIN_TYPE_HANDS_ON}
                      disabled={data.out_of_handon_slots}
                    >
                      I am a clinician and will join hands-on with my personal
                      AVW workstation
                      {data.out_of_handon_slots && ': no more seats available'}
                    </Radio>
                    <Radio value={JOIN_TYPE_VIEWER}>
                      I will join as a viewer, listening into the session
                    </Radio>
                    <Radio value={JOIN_TYPE_RECORDING}>
                      I will not join the workshop but would like to receive the
                      recording of the session
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}
          </Row>
          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              className='register-form-button'
              loading={registerMutation.isPending}
              style={{
                backgroundColor: bgColor
              }}
            >
              {shouldWaitlist ? 'Join Waitlist' : 'Register'}
            </Button>
          </Form.Item>
        </Form>
      )}
    </CourseRegistrationLayout>
  )
}

CourseRegistrationForm.propTypes = {}

export default CourseRegistrationForm
